import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Check, Trash2, X } from "lucide-react";
import clsx from "clsx";
import {
  useNotifications,
  useUnreadNotificationCount,
  useMarkNotificationRead,
  useMarkAllNotificationsRead,
  useDeleteNotification,
  NOTIFICATION_CATEGORY_ROUTE,
  NOTIFICATION_CATEGORY_PORTAL_ROUTE,
  NotificationRecord,
} from "@/api/notifications";

const TYPE_DOT: Record<string, string> = {
  INFO: "bg-blue-500",
  WARNING: "bg-amber-500",
  URGENT: "bg-red-500",
  SUCCESS: "bg-green-500",
};

function timeAgo(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return "hace un momento";
  if (minutes < 60) return `hace ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `hace ${hours} h`;
  const days = Math.floor(hours / 24);
  return `hace ${days} d`;
}

/**
 * Campana de notificaciones (Fase 20), reutilizada tanto en el layout
 * interno (AppLayout) como en el del Portal del cliente (PortalLayout) —
 * el endpoint `/api/notifications` es común a ambos y siempre está acotado
 * al propio usuario autenticado (ver excepción en tenantScope).
 */
export default function NotificationBell({ portal = false }: { portal?: boolean }) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const { data: unreadCount } = useUnreadNotificationCount();
  const { data, isLoading } = useNotifications({ pageSize: 10 });
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();
  const deleteNotification = useDeleteNotification();

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const notifications = data?.data ?? [];
  const count = unreadCount ?? 0;

  const handleClick = async (notification: NotificationRecord) => {
    if (!notification.isRead) {
      await markRead.mutateAsync(notification.id);
    }
    const routeTable = portal ? NOTIFICATION_CATEGORY_PORTAL_ROUTE : NOTIFICATION_CATEGORY_ROUTE;
    setOpen(false);
    navigate(routeTable[notification.category]);
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        className="relative rounded-md p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
        onClick={() => setOpen((v) => !v)}
        aria-label="Notificaciones"
      >
        <Bell size={19} />
        {count > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-semibold text-white">
            {count > 9 ? "9+" : count}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 z-50 mt-2 w-96 max-w-[90vw] overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg">
          <div className="flex items-center justify-between border-b border-gray-100 px-4 py-2.5">
            <h3 className="text-sm font-semibold text-gray-900">Notificaciones</h3>
            {count > 0 && (
              <button
                type="button"
                className="inline-flex items-center gap-1 text-xs font-medium text-brand-600 hover:text-brand-800"
                onClick={() => markAllRead.mutate()}
                disabled={markAllRead.isPending}
              >
                <Check size={12} /> Marcar todas leídas
              </button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {isLoading && <p className="p-4 text-sm text-gray-400">Cargando...</p>}
            {!isLoading && notifications.length === 0 && (
              <p className="p-6 text-center text-sm text-gray-400">No tiene notificaciones.</p>
            )}
            {notifications.map((n) => (
              <div
                key={n.id}
                className={clsx(
                  "flex items-start gap-2.5 border-b border-gray-50 px-4 py-3 last:border-b-0 hover:bg-gray-50",
                  !n.isRead && "bg-brand-50/40"
                )}
              >
                <button type="button" className="flex flex-1 items-start gap-2.5 text-left" onClick={() => handleClick(n)}>
                  <span className={clsx("mt-1.5 h-2 w-2 shrink-0 rounded-full", TYPE_DOT[n.type] ?? "bg-gray-400")} />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium text-gray-900">{n.title}</span>
                    <span className="block text-xs text-gray-500">{n.message}</span>
                    <span className="mt-0.5 block text-[11px] text-gray-400">{timeAgo(n.createdAt)}</span>
                  </span>
                </button>
                <button
                  type="button"
                  className="shrink-0 rounded p-1 text-gray-300 hover:bg-gray-100 hover:text-red-600"
                  title="Eliminar"
                  onClick={() => deleteNotification.mutate(n.id)}
                >
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
          </div>

          <button
            type="button"
            className="flex w-full items-center justify-center gap-1 border-t border-gray-100 py-2 text-xs text-gray-400 hover:bg-gray-50"
            onClick={() => setOpen(false)}
          >
            <X size={12} /> Cerrar
          </button>
        </div>
      )}
    </div>
  );
}
